import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { merge } from "lodash-es";

import { copy } from "shared/assets";
import { Card, Loader, InfoBlockForm, Button } from "shared/components";
import { Spacer } from "shared/layout";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IStatus, IInfoBlock, ITheme, IInfoBlockAPIOrigin } from "shared/types";

import {
  BAND_ACTIONS,
  EventBlocks,
  IEventInfoBlock,
  IEventInfoBlockParams,
  IIconBandSetting,
} from "Bands/types";
import { useEventContext } from "Bands/contexts";
import {
  clearActionMeta,
  createEventInfoBlock,
  dataMetaSelector,
  editEventInfoBlock,
} from "Bands/state";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { getIcon } from "shared/utils";
import { EditInfoBlockItems } from "Bands/containers";
import CopyBlockModal from "Bands/pages/copy-block/CopyBlockModal";

interface EditInfoBlockProps {
  infoBlock?: Omit<IEventInfoBlock, "type">;
  infoBlockType: IIconBandSetting;
  handleFinishEditing?: () => void;
}

export const EditInfoBlock: React.FC<EditInfoBlockProps> = ({
  infoBlock,
  infoBlockType,
  handleFinishEditing,
}) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState({
    title: infoBlock?.title || infoBlockType.label,
    text: infoBlock?.text || "",
  });

  const { userPermissions, eventId } = useEventContext();

  const [apiOrigin, setApiOrigin] = useState<IInfoBlockAPIOrigin>(IInfoBlockAPIOrigin.Form);

  const [action, setAction] = useState<string>();

  useClearActionMetaOnUnMount(action || "");

  const { api: apiMeta } = useSelector(dataMetaSelector(action || ""));

  useEffect(() => {
    if (action === undefined) {
      setAction(infoBlock ? BAND_ACTIONS.editEventInfoBlock : BAND_ACTIONS.createEventInfoBlock);
    }
  }, [infoBlock, action]);

  // successful api call
  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && action) {
      // if API is called from the form, show toast message
      if (apiOrigin === IInfoBlockAPIOrigin.Form) {
        dispatch(clearActionMeta(action));
        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: `${
              action === BAND_ACTIONS.editEventInfoBlock ? copy.actions.edited : copy.actions.added
            } ${infoBlockType?.label}`,
          })
        );
      }

      // update action
      setAction(infoBlock ? BAND_ACTIONS.editEventInfoBlock : BAND_ACTIONS.createEventInfoBlock);

      // clear delete action in case edit was empty and therefore delete was dispatched
      dispatch(clearActionMeta(BAND_ACTIONS.deleteEventInfoBlock));
    }
  }, [apiMeta?.status, action, dispatch, infoBlock, infoBlockType, apiOrigin]);

  const handleSubmit = (data: Partial<IInfoBlock>) => {
    if (!eventId) return;

    const mergedData = {
      ...infoBlock,
      title: fields.title || infoBlockType.label,
      text: fields.text || infoBlock?.text || "",
      ...data,
      type: API_ENDPOINTS.EVENT_INFO_BLOCK_TYPE(infoBlockType.id),
      event: API_ENDPOINTS.EVENT(eventId),
    } as IEventInfoBlockParams;

    if (infoBlock) {
      dispatch(editEventInfoBlock({ id: infoBlock.id, data: mergedData }));
    } else {
      dispatch(createEventInfoBlock(mergedData));
    }
  };

  const clearInfoBlockMeta = useCallback(() => {
    if (action) {
      dispatch(clearActionMeta(action));
    }
  }, [dispatch, action]);

  const defaultValues = useMemo(
    () => merge({}, { title: infoBlockType.label }, infoBlock),
    [infoBlock, infoBlockType.label]
  );

  if (action === "") return <Loader />;

  return (
    <Card
      icon={getIcon(infoBlockType.icon)}
      title={`${
        action === BAND_ACTIONS.editEventInfoBlock ? copy.actions.editing : copy.actions.adding
      } ${infoBlockType?.label}`}
    >
      <Spacer size="l">
        <CopyBlockModal
          blockType={EventBlocks.infoBlock}
          infoBlockType={infoBlockType}
          deleteBlockId={infoBlock?.id}
        />
        <InfoBlockForm
          showTitle
          handleSubmit={handleSubmit}
          setFields={setFields}
          blockExists={action === BAND_ACTIONS.editEventInfoBlock}
          apiStatus={apiOrigin === IInfoBlockAPIOrigin.Form ? apiMeta?.status : undefined}
          hasError={!!apiMeta?.error && apiOrigin === IInfoBlockAPIOrigin.Form}
          defaultValues={defaultValues}
        />
        <EditInfoBlockItems
          infoBlockLabel={infoBlockType.label}
          defaultValues={infoBlock}
          handleSubmit={handleSubmit}
          infoBlockApiStatus={apiMeta?.status}
          infoBlockHasError={!!apiMeta?.error}
          apiOrigin={apiOrigin}
          setApiOrigin={setApiOrigin}
          clearInfoBlockMeta={clearInfoBlockMeta}
          userPermissions={userPermissions}
        />
        {handleFinishEditing && (
          <Button onClick={handleFinishEditing} theme={ITheme.secondary}>
            {copy.form.finish}
          </Button>
        )}
      </Spacer>
    </Card>
  );
};
